/**
 * GlobalNavPalette
 *
 * Single ⌘K / Ctrl+K palette for the whole app:
 *   - Default sections: Nav (workflows / apps / projects / pages / account).
 *   - When a route registers extras via PaletteExtensions.Register, the
 *     palette also renders Actions and Ask-AI prompt rows above the nav.
 *
 * One mount (RootLayout), one keyboard binding, one focus trap. Designer-
 * specific extras (workflow actions, node-aware AI prompts, free-form AI
 * submit) flow in through context — the palette itself stays decoupled
 * from any route's data model.
 *
 * A11y
 *   - role="dialog" + aria-modal; focus moves to the input, restored on close
 *   - role="listbox" / role="option" with a single contiguous index range
 *     that spans Actions → Ask AI → Nav (one cursor, predictable Up/Down)
 *   - Escape closes from anywhere inside the dialog; body scroll locks while open
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { mergeClasses } from '@coreai/fluentui-react';
import {
  ArrowEnterRegular,
  Apps20Regular,
  ChevronUpDownRegular,
  DismissRegular,
  Document20Regular,
  FlashRegular,
  Flow20Regular,
  Folder20Regular,
  List20Regular,
  Person20Regular,
  SearchRegular,
  SendRegular,
  SparkleRegular,
} from '@fluentui/react-icons';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useTelemetry } from '@/hooks/useTelemetry';
import { formatShortcut } from '@/utils/input';
import { capPerCategory, groupByCategory, rankCommands } from '@/utils/search';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  CATEGORY_SUBTITLES,
  useNavCommands,
  type NavCommand,
  type NavCommandCategory,
} from './useNavCommands.hooks';
import { usePaletteStyles } from './paletteStyles';
import {
  usePaletteExtensions,
  usePaletteHandle,
  type PaletteAction,
  type PaletteQuickAction,
} from './paletteExtensions';

const CATEGORY_ICON: Record<NavCommandCategory, React.ReactNode> = {
  pages: <Document20Regular />,
  projects: <Folder20Regular />,
  apps: <Apps20Regular />,
  workflows: <Flow20Regular />,
  account: <Person20Regular />,
};

function pickNavIcon(cmd: NavCommand): React.ReactNode {
  if (cmd.icon) return cmd.icon;
  if (cmd.id === 'projects:list' || cmd.id === 'apps:list') return <List20Regular />;
  return CATEGORY_ICON[cmd.category];
}

/**
 * Substring filter for action/ask rows that don't have a dedicated ranker.
 * These lists are tiny (typically < 10), so a linear `includes` is fine
 * and keeps the behavior predictable for the user.
 */
function matchesQuery(label: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return label.toLowerCase().includes(q);
}

/**
 * Discriminated row union for the unified visible list. Keeping all rows
 * in one ordered array gives us a single keyboard cursor and a contiguous
 * `aria-activedescendant` index range across heterogeneous sections.
 */
type PaletteRow =
  | { kind: 'action'; key: string; payload: PaletteAction }
  | { kind: 'ask'; key: string; payload: PaletteQuickAction }
  | { kind: 'nav'; key: string; payload: NavCommand };

export const GlobalNavPalette = () => {
  const styles = usePaletteStyles();
  // Pulled from the single useCommandPalette instance that lives in
  // PaletteExtensionsProvider. No mirror effect, no second listener.
  const { isOpen, close } = usePaletteHandle();
  const { trackEvent } = useTelemetry();
  const extensions = usePaletteExtensions();

  // Gate query fetching behind `isOpen` so the palette is essentially free
  // when closed. The pills already share the same query keys (cached by
  // tanstack-query), so the first open hits warm cache.
  const navCommands = useNavCommands({ enabled: isOpen });

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const askAi = extensions.askAi;
  const actions = extensions.actions;
  const onAskSubmit = extensions.onAskSubmit;
  const hasAskSubmit = typeof onAskSubmit === 'function';

  // Build the visible row list.
  const visibleActions = useMemo(
    () => (actions ?? []).filter((a) => matchesQuery(a.label, query)),
    [actions, query],
  );
  const visibleAsk = useMemo(
    () => (askAi && hasAskSubmit ? askAi.items.filter((a) => matchesQuery(a.label, query)) : []),
    [askAi, hasAskSubmit, query],
  );

  const rankedNav = useMemo(() => rankCommands(navCommands, query), [navCommands, query]);
  const isQuerying = query.trim().length > 0;
  const cappedNav = useMemo(
    () =>
      capPerCategory(groupByCategory(rankedNav, CATEGORY_ORDER), isQuerying ? 50 : 4, {
        // Pages is statically bounded (5 routes); never truncate it.
        pages: 50,
      }),
    [rankedNav, isQuerying],
  );

  const visibleNav = useMemo(() => {
    const out: NavCommand[] = [];
    for (const { items } of cappedNav.values()) out.push(...items);
    return out;
  }, [cappedNav]);

  const visibleRows = useMemo<PaletteRow[]>(() => {
    const rows: PaletteRow[] = [];
    visibleActions.forEach((a) => rows.push({ kind: 'action', key: a.id, payload: a }));
    visibleAsk.forEach((a) => rows.push({ kind: 'ask', key: a.id, payload: a }));
    visibleNav.forEach((c) => rows.push({ kind: 'nav', key: c.id, payload: c }));
    return rows;
  }, [visibleActions, visibleAsk, visibleNav]);

  // Map "{kind}:{key}" → flat index so render can resolve each row's
  // listbox id without walking the flattening logic twice.
  const flatIndexByKey = useMemo(() => {
    const map = new Map<string, number>();
    visibleRows.forEach((r, i) => map.set(`${r.kind}:${r.key}`, i));
    return map;
  }, [visibleRows]);

  // Focus trap + restore.
  useFocusTrap({ active: isOpen, containerRef: dialogRef, initialFocusRef: inputRef });

  // Reset transient state and lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setActiveIndex(0);
    trackEvent('nav_palette.opened');

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, trackEvent]);

  // Clamp active index whenever the visible list shrinks.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(visibleRows.length - 1, 0)));
  }, [visibleRows.length]);

  // Reset to the first row when the user starts typing — "fresh search"
  // semantics shared with VS Code / Linear / Raycast.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Scroll the active row into view as ↑/↓ moves the highlight.
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const active = listRef.current.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`,
    );
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, activeIndex]);

  const submitAsk = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed || !onAskSubmit) return;
    trackEvent('nav_palette.ask.submitted');
    onAskSubmit(trimmed);
    setQuery('');
    close();
  }, [query, onAskSubmit, trackEvent, close]);

  const executeRow = useCallback(
    (row: PaletteRow) => {
      switch (row.kind) {
        case 'action':
          trackEvent('nav_palette.action.selected', { id: row.payload.id });
          row.payload.execute();
          close();
          return;
        case 'ask':
          if (!onAskSubmit) return;
          trackEvent('nav_palette.ask.prompt.selected', { id: row.payload.id });
          onAskSubmit(row.payload.prompt);
          close();
          return;
        case 'nav':
          trackEvent('nav_palette.command.executed', {
            commandId: row.payload.id,
            category: row.payload.category,
          });
          row.payload.execute();
          close();
          return;
      }
    },
    [trackEvent, close, onAskSubmit],
  );

  const executeAt = useCallback(
    (index: number) => {
      const row = visibleRows[index];
      if (row) executeRow(row);
    },
    [visibleRows, executeRow],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (visibleRows.length > 0) {
          setActiveIndex((i) => Math.min(i + 1, visibleRows.length - 1));
        }
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (visibleRows.length > 0) {
          setActiveIndex((i) => Math.max(i - 1, 0));
        }
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        if (visibleRows.length > 0) setActiveIndex(visibleRows.length - 1);
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        // Run the highlighted row when there is one; otherwise fall back
        // to submitting the query as a free-form AI prompt (only if the
        // current route registered an AI handler). The empty state copy
        // hints "Press Enter to ask the assistant" so this is discoverable.
        const row = visibleRows[activeIndex];
        if (row) {
          event.preventDefault();
          executeRow(row);
          return;
        }
        if (hasAskSubmit) {
          event.preventDefault();
          submitAsk();
        }
      }
    },
    [visibleRows, activeIndex, executeRow, hasAskSubmit, submitAsk],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onDocKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, [isOpen, close]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) close();
    },
    [close],
  );

  if (!isOpen) return null;

  const placeholder = hasAskSubmit
    ? 'Jump to anything, ask AI, run an action…'
    : 'Jump to environment, app, workflow, page…';

  const emptyCopy = query.trim()
    ? hasAskSubmit
      ? `No matches for "${query}" — press Enter to ask the assistant`
      : `No matches for "${query}"`
    : 'No commands available.';

  const activeRow = visibleRows[activeIndex];

  return (
    <div className={styles.overlay} onClick={handleBackdropClick} role="presentation">
      <div
        ref={dialogRef}
        className={styles.container}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        <div className={styles.header}>
          <SearchRegular className={styles.searchIcon} aria-hidden="true" />
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-controls={listboxId}
            aria-expanded
            aria-autocomplete="list"
            aria-activedescendant={activeRow ? `${listboxId}-${activeIndex}` : undefined}
            aria-label={hasAskSubmit ? 'Search or ask AI' : 'Search navigation'}
          />
          <span className={styles.shortcutBadge} aria-hidden="true">
            <ChevronUpDownRegular fontSize={12} />
            navigate
          </span>
          {hasAskSubmit ? (
            <button
              type="button"
              className={mergeClasses(styles.shortcutBadge, styles.shortcutBadgeButton)}
              onClick={() => {
                if (activeRow) executeAt(activeIndex);
                else submitAsk();
              }}
              disabled={!activeRow && !query.trim()}
              aria-label={activeRow ? 'Run selected' : 'Ask AI'}
            >
              {activeRow ? (
                <>
                  <ArrowEnterRegular fontSize={12} />
                  run
                </>
              ) : (
                <>
                  <SendRegular fontSize={12} />
                  ask
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              className={mergeClasses(styles.shortcutBadge, styles.shortcutBadgeButton)}
              onClick={() => {
                if (activeRow) executeAt(activeIndex);
              }}
              disabled={!activeRow}
              aria-label="Run selected command"
            >
              <ArrowEnterRegular fontSize={12} />
              run
            </button>
          )}
          <button
            type="button"
            className={mergeClasses(styles.shortcutBadge, styles.shortcutBadgeButton, styles.closeButton)}
            onClick={close}
            aria-label="Close (Escape)"
          >
            <DismissRegular fontSize={12} />
          </button>
        </div>

        <div
          ref={listRef}
          className={styles.results}
          id={listboxId}
          role="listbox"
          aria-label="Palette results"
        >
          {visibleRows.length === 0 ? (
            <div className={styles.empty}>{emptyCopy}</div>
          ) : (
            renderRows({
              visibleActions,
              visibleAsk,
              cappedNav,
              flatIndexByKey,
              activeIndex,
              listboxId,
              styles,
              askSubtitle: askAi?.subtitle,
              setActiveIndex,
              executeAt,
            })
          )}
        </div>

        <div className={styles.footer} aria-hidden="true">
          <span>
            <span className={styles.footerKey}>↑↓</span> navigate
          </span>
          <span>
            <span className={styles.footerKey}>↵</span> {hasAskSubmit ? 'run / ask' : 'open'}
          </span>
          <span>
            <span className={styles.footerKey}>esc</span> close
          </span>
          <span className={styles.footerEnd}>
            <span className={styles.footerKey}>{formatShortcut('K')}</span> toggle
          </span>
        </div>
      </div>
    </div>
  );
};

interface RenderRowsParams {
  visibleActions: PaletteAction[];
  visibleAsk: PaletteQuickAction[];
  cappedNav: Map<NavCommandCategory, { items: NavCommand[]; truncated: number }>;
  flatIndexByKey: Map<string, number>;
  activeIndex: number;
  listboxId: string;
  styles: ReturnType<typeof usePaletteStyles>;
  askSubtitle: string | undefined;
  setActiveIndex: (index: number) => void;
  executeAt: (index: number) => void;
}

function renderRows({
  visibleActions,
  visibleAsk,
  cappedNav,
  flatIndexByKey,
  activeIndex,
  listboxId,
  styles,
  askSubtitle,
  setActiveIndex,
  executeAt,
}: RenderRowsParams) {
  const blocks: React.ReactNode[] = [];

  if (visibleActions.length > 0) {
    blocks.push(
      <div key="actions" role="group" aria-label="Actions">
        <div className={styles.groupHeader} aria-hidden="true">
          <span className={styles.groupHeaderTitle}>Actions</span>
        </div>
        {visibleActions.map((action) => {
          const idx = flatIndexByKey.get(`action:${action.id}`) ?? -1;
          const isActive = idx === activeIndex;
          return (
            <button
              type="button"
              key={action.id}
              id={`${listboxId}-${idx}`}
              data-row-index={idx}
              role="option"
              aria-selected={isActive}
              tabIndex={-1}
              className={mergeClasses(styles.row, isActive && styles.rowActive)}
              onClick={() => executeAt(idx)}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className={styles.rowIcon} aria-hidden="true">
                {action.icon ?? <FlashRegular />}
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowLabel}>{action.label}</span>
              </span>
              {action.shortcut && (
                <span className={styles.rowShortcut} aria-hidden="true">
                  {action.shortcut}
                </span>
              )}
            </button>
          );
        })}
      </div>,
    );
  }

  if (visibleAsk.length > 0) {
    blocks.push(
      <div key="ask" role="group" aria-label="Ask AI">
        <div className={styles.groupHeader} aria-hidden="true">
          <span className={styles.groupHeaderTitle}>Ask AI</span>
          {askSubtitle && (
            <span className={styles.groupHeaderSubtitle}>· {askSubtitle}</span>
          )}
        </div>
        {visibleAsk.map((action) => {
          const idx = flatIndexByKey.get(`ask:${action.id}`) ?? -1;
          const isActive = idx === activeIndex;
          return (
            <button
              type="button"
              key={action.id}
              id={`${listboxId}-${idx}`}
              data-row-index={idx}
              role="option"
              aria-selected={isActive}
              tabIndex={-1}
              className={mergeClasses(styles.row, isActive && styles.rowActive)}
              onClick={() => executeAt(idx)}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className={styles.rowIcon} aria-hidden="true">
                <SparkleRegular />
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowLabel}>{action.label}</span>
              </span>
            </button>
          );
        })}
      </div>,
    );
  }

  for (const [category, { items, truncated }] of cappedNav.entries()) {
    if (items.length === 0) continue;
    const subtitle = CATEGORY_SUBTITLES[category];
    blocks.push(
      <div key={`nav-${category}`} role="group" aria-label={CATEGORY_LABELS[category]}>
        <div className={styles.groupHeader} aria-hidden="true">
          <span className={styles.groupHeaderTitle}>{CATEGORY_LABELS[category]}</span>
          {subtitle && (
            <span className={styles.groupHeaderSubtitle}>· {subtitle}</span>
          )}
        </div>
        {items.map((cmd) => {
          const idx = flatIndexByKey.get(`nav:${cmd.id}`) ?? -1;
          const isActive = idx === activeIndex;
          return (
            <button
              type="button"
              key={cmd.id}
              id={`${listboxId}-${idx}`}
              data-row-index={idx}
              role="option"
              aria-selected={isActive}
              tabIndex={-1}
              className={mergeClasses(styles.row, isActive && styles.rowActive)}
              onClick={() => executeAt(idx)}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className={styles.rowIcon} aria-hidden="true">
                {pickNavIcon(cmd)}
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowLabel}>{cmd.label}</span>
                {cmd.hint && <span className={styles.rowHint}>{cmd.hint}</span>}
              </span>
              {cmd.current && <span className={styles.rowCurrentTag}>Current</span>}
            </button>
          );
        })}
        {truncated > 0 && (
          <div className={styles.groupTruncated} aria-hidden="true">
            +{truncated} more — keep typing to filter
          </div>
        )}
      </div>,
    );
  }

  return blocks;
}
